import React, { useRef, useEffect } from 'react';

const Contact = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="relative py-28 px-6 overflow-hidden bg-transparent"
    >
      {/* Background Tech Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-1/4 left-[10%] w-32 h-32 border-4 border-blue-500/10 rounded-full animate-pulse"></div>
        <div className="absolute bottom-1/3 right-[15%] w-24 h-24 border-3 border-cyan-500/10 rounded-full animate-spin-slow opacity-70"></div>
        <div className="absolute top-1/3 right-[25%] w-16 h-16 border-t-4 border-r-4 border-blue-500/10 transform rotate-45 animate-wave"></div>
        <div className="absolute bottom-1/4 left-[25%] w-20 h-20 border-2 border-purple-500/10 rounded-lg transform rotate-12 animate-float-1"></div>
      </div>

      {/* Section Content */}
      <div className="max-w-4xl mx-auto relative z-10 text-center">
        {/* Animated Header */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
            Get In Touch
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto mb-8">
            I'm currently looking for new opportunities and open to collaboration. Feel free to reach out!
          </p>
          <div className="w-40 h-1 mx-auto bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"></div>
        </div>

        {/* Contact Card */}
        <div className="bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700 shadow-xl hover:shadow-blue-500/20 transition-all duration-300 p-8 md:p-10 relative group">
          {/* Decorative Corners */}
          <div className="absolute top-0 left-0 w-12 h-12 overflow-hidden opacity-20 group-hover:opacity-40 transition-opacity">
            <div className="absolute top-0 left-0 w-24 h-24 bg-blue-500/10 transform rotate-45 origin-top-left translate-x-[-50%] translate-y-[-50%]"></div>
          </div>
          <div className="absolute bottom-0 right-0 w-12 h-12 overflow-hidden opacity-20 group-hover:opacity-40 transition-opacity">
            <div className="absolute bottom-0 right-0 w-24 h-24 bg-cyan-500/10 transform rotate-45 origin-bottom-right translate-x-[50%] translate-y-[50%]"></div>
          </div>

          {/* Content */}
          <div className="relative z-10">
            <h3 className="text-2xl md:text-3xl font-semibold mb-6">
              Have a question or want to work together?
            </h3>

            {/* Email Button */}
            <a
              href="mailto:aniketofficial540@gmail.com"
              className="inline-block bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-semibold px-6 py-3 rounded-lg shadow-lg hover:shadow-blue-500/30 transition-all duration-300 mb-10 transform hover:scale-105"
            >
              Say Hello 🚀
            </a>

            {/* Social Links */}
            <div className="flex justify-center space-x-8 mt-10">
              <a
                href="https://github.com/aniketofficial540"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center transition-transform duration-300 hover:scale-110"
                aria-label="GitHub"
              >
                <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-blue-600 transition-colors duration-300">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path
                      d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.826c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.579.688.481A10.019 10.019 0 0122 12.017c0-5.533-4.477-10.017-10-10.017z"
                    />
                  </svg>
                </div>
                <span className="mt-2 text-sm text-gray-400 group-hover:text-white transition-colors">GitHub</span>
              </a>

              <a
                href="https://www.linkedin.com/in/aniket-adarsh/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center transition-transform duration-300 hover:scale-110"
                aria-label="LinkedIn"
              >
                <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-blue-600 transition-colors duration-300">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.962 0-1.74-.79-1.74-1.764s.778-1.764 1.74-1.764 1.74.79 1.74 1.764-.778 1.764-1.74 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                </div>
                <span className="mt-2 text-sm text-gray-400 group-hover:text-white transition-colors">LinkedIn</span>
              </a>

              <a
                href="https://x.com/_aniket_adarsh"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center transition-transform duration-300 hover:scale-110"
                aria-label="Twitter"
              >
                <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-blue-600 transition-colors duration-300">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.962.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482c0 .39.059 .772.177 1.125C6.83 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.061a4.923 4.923 0 003.946 4.827c-.203.004-.407-.008-.61-.033a4.908 4.908 0 01-1.404-1.255c0 3.951 2.8 7.223 6.496 7.908-.477.128-.98.19-1.505.19-.367 0-.724-.021-1.073-.067a4.96 4.96 0 004.76 3.419c5.412 0 8.34-4.491 8.34-8.34 0-.128-.002-.254-.009-.379a5.932 5.932 0 001.484-1.515 4.897 4.897 0 01-.449.033c.368-.217.697-.507.963-.851-.344.064-.697.109-1.055.135a4.923 4.923 0 002.16-2.724 9.937 9.937 0 00-7.166-3.646c-1.432 0-2.742.383-3.865 1.016a3.588 3.588 0 00-.278 1.79 10.019 10.019 0 01-7.176-3.644l-.002-.001a4.92 4.92 0 00-.08 2.392 4.907 4.907 0 001.432-1.64c-.952-.094-1.797-.649-2.324-1.45 0 4.687 3.264 8.66 7.53 9.64a4.917 4.917 0 01-2.212.085c.629-.392 1.342-.614 2.08-.614 .5 0 .98.071 1.42.2a4.935 4.935 0 01-3.94 4.827c-.256 0-.51-.015-.76-.043a4.927 4.927 0 004.486 3.42 9.86 9.86 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.054 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </div>
                <span className="mt-2 text-sm text-gray-400 group-hover:text-white transition-colors">Twitter</span>
              </a>
            </div>

            {/* Decorative Line */}
            <div className="mt-12 w-16 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 mx-auto"></div>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes wave {
          0%, 100% { transform: rotate(45deg) scaleX(1); }
          50% { transform: rotate(45deg) scaleX(1.5); }
        }

        @keyframes float-1 {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }

        .animate-fade-in {
          opacity: 0;
          transform: translateY(30px);
          animation: fade-in 0.8s ease-out forwards;
        }

        .animate-spin-slow {
          animation: spin-slow 25s linear infinite;
        }

        .animate-wave {
          animation: wave 5s ease-in-out infinite;
        }

        .animate-float-1 {
          animation: float-1 6s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default Contact;